export type ActorType = 'user' | 'service_account';

export interface Actor {
  type: ActorType;
  id: string;
}

/**
 * The closed set of actions the platform supports, in canonical render order.
 * Strictly typed on purpose: a new action must be added here (and wired into the
 * effective-access UI), never introduced ad-hoc per resource. A resource opts
 * into a subset of these; any action it omits renders as `N/A`.
 */
export const ACTIONS = ['create', 'read', 'edit', 'delete', 'archive'] as const;
export type Action = (typeof ACTIONS)[number];

/** A permission is always "{resourceType}:{action}", e.g. "order:read". */
export type PermissionKey = `${string}:${Action}` | `${string}:${string}`;

export interface ResourceRef {
  type: string;
  id: string;
}

export type Effect = 'allow' | 'deny';

export interface PermissionRow {
  id: string;
  tenantId: string;
  key: string;
  resourceType: string;
  action: string;
  name: string | null;
}

export interface RecordException {
  actorType: ActorType;
  actorId: string;
  permission: PermissionKey;
  effect: Effect;
}

/** One per-record exception held by an actor — for the actor detail panel. */
export interface ActorException {
  permission: PermissionKey;
  resourceType: string;
  resourceId: string;
  effect: Effect;
}

/** A resource type in the catalog (code-defined, dev-controlled). */
export interface ResourceView {
  key: string;
  name: string;
  description: string | null;
}

/** A service-account actor. */
export interface ServiceAccountView {
  id: string;
  name: string;
  disabled: boolean;
}

/** An actor plus the role keys it holds — for the management Actors table. */
export interface ActorWithRoles {
  actorType: ActorType;
  actorId: string;
  displayName: string | null;
  roleKeys: string[];
}

export interface RoleView {
  id: string;
  key: string;
  name: string;
  description: string | null;
  isSystem: boolean;
}

export interface ActorPermissions {
  /** Flat set of type-level permissions the actor holds, e.g. ['order:read', 'style:approve']. */
  permissions: PermissionKey[];
  /** Grouped for UI: resourceType → actions, e.g. { order: ['read','edit'], style: ['read'] }. */
  byResource: Record<string, string[]>;
}

/** One resource in the permission blueprint, with the actions it supports. */
export interface BlueprintResource {
  key: string;
  name: string;
  description: string | null;
  /** The subset of ACTIONS this resource supports; the rest render as `N/A`. */
  actions: Action[];
}

/** A UI grouping of resources (Network, Products, Vendor Compliance, …). */
export interface BlueprintFeatureGroup {
  key: string;
  name: string;
  description: string | null;
  resources: BlueprintResource[];
}

/** A reusable action preset applied when authoring a permission set. */
export interface PermissionTemplateView {
  key: string;
  name: string;
  description: string | null;
  /** Actions the template grants; effective per resource = intersect w/ supported. */
  actions: Action[];
}

/**
 * The full permission catalog for the authoring UI: every feature group and its
 * resources (with supported actions) plus the reusable templates. One payload
 * drives both the permission-set editor grid and the effective-access matrix.
 */
export interface PermissionBlueprint {
  actions: Action[];
  groups: BlueprintFeatureGroup[];
  templates: PermissionTemplateView[];
}

/**
 * A predefined role's grant on ONE resource: `'all'` = every action that
 * resource supports; otherwise the explicit subset of that resource's actions.
 */
export type CatalogResourceGrant = 'all' | readonly Action[];

/**
 * A code-defined resource type. `actions` is the subset of the strict `ACTIONS`
 * set this resource supports (the rest render as `N/A`); grants live on the
 * resource because supported-action sets differ per resource. `group` ties it to
 * a feature group for the authoring UI.
 */
export interface CatalogResource {
  key: string;
  name: string;
  description?: string;
  /** Feature-group key this resource belongs to. */
  group: string;
  actions: readonly Action[];
  /** predefined role key → which of THIS resource's actions that role gets */
  grants: Readonly<Record<string, CatalogResourceGrant>>;
}

/** A predefined (system) role — identity only; grants live on each resource. */
export interface CatalogRole {
  key: string;
  name: string;
}

/** A UI grouping of resources; owns display name/description/order only. */
export interface CatalogFeatureGroup {
  key: string;
  name: string;
  description?: string;
  order: number;
}

/**
 * A reusable action preset. When applied to a resource the effective grant is
 * `actions ∩ resource.actions`, so unsupported (`N/A`) actions are skipped.
 */
export interface PermissionTemplate {
  key: string;
  name: string;
  description?: string;
  actions: readonly Action[];
}

export interface TenantCatalog {
  groups: readonly CatalogFeatureGroup[];
  resources: readonly CatalogResource[];
  roles: readonly CatalogRole[];
  templates: readonly PermissionTemplate[];
}
